import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";

const OK_CODE = "001";
const DEFAULT_ERROR_MESSAGE =
  "An error occured. Please check your credit card information and try again.";

class HostedMonerisController extends Controller {
  static outlets = ["payment-update"];
  static values = {
    monerisTokenizeUrl: String,
    monerisIframeDesktopUrl: String,
    monerisIframeMobileUrl: String,
  };
  static targets = ["monerisIframe", "dataKey", "responseCode", "errorMessage", "disabledOverlay"];

  static MOBILE_GRID_BREAKPOINT = 768;

  connect() {
    this.#setIframeUrl();

    window.addEventListener("message", this.onTokenizeComplete, false);

    // We're not a parent of this element, so we can't reference as a target
    this.locationSelect = document.getElementById("location-select");
    if (this.locationSelect) {
      this.locationSelect.addEventListener("change", this.onLocationSelection, true);

      // Initialize ourselves disabled/enabled
      this.setDisabledOverlay();
    }

    this.paymentUpdateOutlet.addAsyncSubmitInterrupt(this.handleSubmit);
    this.paymentUpdateOutlet.enableSubmit();
  }

  disconnect() {
    if (this.locationSelect) {
      this.locationSelect.removeEventListener("change", this.onLocationSelection, true);
    }
    window.removeEventListener("message", this.onTokenizeComplete);
    this.paymentUpdateOutlet.removeAsyncSubmitInterrupt(this.handleSubmit);
  }

  handleSubmit = () => {
    // If we're still waiting on a tokenization response, we'll already have a pending resolve.
    // Resolve with a failure before proceeding.
    if (this.pendingResolve) {
      this.#resolvePendingPromise(false);
    }

    this.monerisIframeTarget.contentWindow.postMessage("tokenize", this.monerisTokenizeUrlValue);

    // The promise will get resolved when we receive the tokenize completion message
    // from the iFrame
    return new Promise((resolve, _reject) => (this.pendingResolve = resolve));
  };

  onTokenizeComplete = (event) => {
    if (
      !this.monerisIframeTarget.src.startsWith(event.origin) ||
      !event.isTrusted ||
      !this.pendingResolve
    ) {
      return;
    }

    const {
      errorMessage,
      dataKey,
      responseCode: responseCodeStringOrArray,
    } = JSON.parse(event.data);
    const responseCodeArray = [].concat(responseCodeStringOrArray);
    const responseCodeString = responseCodeArray[0];

    if (location.hostname.endsWith("localtest.rinsed.co")) {
      // We don't have an API key to setup the test environment, so all tokenizations
      // will fail. We'll treat those as successes.
      this.dataKeyTarget.value = "moneris-temp-key-123";
      this.responseCodeTarget.value = JSON.stringify(["001"]);
      return this.#resolvePendingPromise(true);
    }

    if (responseCodeString && responseCodeString !== OK_CODE) {
      return this.#tokenizationError(this.#errorMessagerForCode(responseCodeString));
    }

    if (errorMessage) {
      return this.#tokenizationError(errorMessage);
    }

    if (!dataKey || !responseCodeString) {
      return this.#tokenizationError(DEFAULT_ERROR_MESSAGE);
    }

    this.dataKeyTarget.value = dataKey;
    this.responseCodeTarget.value = JSON.stringify(responseCodeArray);
    this.#resolvePendingPromise(true);
  };

  onLocationSelection = () => {
    const url = new URL(window.location.href);

    if (this.#isLocationSelected()) {
      url.searchParams.set("patheon_site_id", this.locationSelect.value);
    }

    Turbo.visit(url, { frame: "hosted_moneris_form" });
    this.setDisabledOverlay();
  };

  setDisabledOverlay() {
    if (this.#isLocationSelected()) {
      this.disabledOverlayTarget.classList.add("hidden");
    } else {
      this.disabledOverlayTarget.classList.remove("hidden");
    }
  }

  #tokenizationError(errorMessage) {
    this.paymentUpdateOutlet.setCustomError(errorMessage);
    this.#resolvePendingPromise(false);
  }

  #resolvePendingPromise(value) {
    this.pendingResolve(value);
    this.pendingResolve = null;
  }

  #setIframeUrl() {
    const isMobile =
      this.monerisIframeTarget.clientWidth < HostedMonerisController.MOBILE_GRID_BREAKPOINT;

    this.monerisIframeTarget.src = isMobile
      ? this.monerisIframeMobileUrlValue
      : this.monerisIframeDesktopUrlValue;
  }

  #errorMessagerForCode(code) {
    const target = this.errorMessageTargets.find((el) => el.dataset.code === code);

    return target ? target.dataset.message : DEFAULT_ERROR_MESSAGE;
  }

  #isLocationSelected() {
    return !!(
      this.locationSelect.value &&
      this.locationSelect.value !== "null" &&
      this.locationSelect.value.length
    );
  }
}

export default HostedMonerisController;
